import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Note, VarCard, Warning } from '../../touchpose/ui';
import quadTopologyImg from '../../../assets/images/squarebit-subdivs/docs/quad-topology-data.png';

// i18n contract for the Subdivs docs: every Unreal component name, property
// name, panel name, button label and menu path lives in a `components` slot (or
// an interpolated {{value}} for string-only props like image alt text), never
// inside the translated string. Unreal's editor ships in English, so a
// translated property name makes the docs impossible to follow.
//
// Slot names must never be an HTML void element (link, img, br, col, meta,
// param, source, ...): `<link>text</link>` parses as self-closing and the text
// is silently dropped. Use `usec`-style names instead.

/** Section heading with an id for the sidebar anchor / scroll-spy. */
export const H2: React.FC<{ id?: string; children: React.ReactNode }> = ({
  id,
  children
}) => (
  <h2
    id={id}
    className="font-display text-[20px] font-bold text-content-primary mt-[42px] mb-[14px] tracking-[-0.3px]"
  >
    {children}
  </h2>
);

/** Sub-heading within a section (for example Alembic / USD under Import settings). */
export const H3: React.FC<{ id?: string; children: React.ReactNode }> = ({
  id,
  children
}) => (
  <h3
    id={id}
    className="font-display text-[16px] font-bold text-content-primary mt-[26px] mb-[10px] tracking-[-0.2px]"
  >
    {children}
  </h3>
);

export const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-[14px] leading-[1.75] text-content-secondary mb-[12px]">
    {children}
  </p>
);

/** Keyboard-key chip, styled to match the MenuPath chip (theme-aware). */
export const Kbd: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <kbd className="font-mono text-[12px] bg-surface-secondary border border-border rounded-[4px] px-[6px] py-[1px] text-content-primary">
    {children}
  </kbd>
);

/**
 * The Ctrl+Alt+<n> chord as one unit. Key names are never translated (they are
 * the physical keys), so the whole chord goes into a single translation slot.
 */
export const KeyCombo: React.FC<{ n: string }> = ({ n }) => (
  <>
    <Kbd>Ctrl</Kbd> + <Kbd>Alt</Kbd> + <Kbd>{n}</Kbd>
  </>
);

/** A screenshot with an optional caption, styled to match the rest of the docs. */
export const DocImage: React.FC<{
  src: string;
  alt: string;
  caption?: string;
}> = ({ src, alt, caption }) => (
  <figure className="m-0 my-[16px]">
    <img
      src={src}
      alt={alt}
      className="max-w-full w-auto rounded-[10px] border border-surface-hover shadow-[0_2px_12px_rgba(34,211,238,0.08),0_1px_4px_rgba(0,0,0,0.06)]"
    />
    {caption && (
      <figcaption className="mt-[8px] text-[12.5px] text-slate-500 leading-[1.55]">
        {caption}
      </figcaption>
    )}
  </figure>
);

/** Shared bullet styling. Numbered steps use <Steps> below. */
export const UL_CLASS =
  'pl-[20px] m-0 mb-[12px] list-disc text-[14px] leading-[1.8] text-content-secondary marker:text-slate-400';

/**
 * Numbered steps as bubbles — the same marker the TouchPose docs use, so a
 * numbered step looks the same wherever it appears on the site.
 *
 * Takes its children as an ARRAY rather than <li> elements: the bubble and the
 * step body are two flex siblings, so the component has to own the <li>.
 */
export const Steps: React.FC<{ children: React.ReactNode }> = ({
  children
}) => (
  <ol className="list-none p-0 m-0 mb-[16px]">
    {React.Children.toArray(children).map((step, i) => (
      <li key={i} className="flex gap-[16px] mb-[20px] items-start">
        <span className="shrink-0 w-[28px] h-[28px] rounded-full bg-surface-hover border-[1.5px] border-border-brand flex items-center justify-center font-display text-[13px] font-bold text-blue-link">
          {i + 1}
        </span>
        <div className="mt-[4px] text-[14px] leading-[1.75] text-content-secondary">
          {step}
        </div>
      </li>
    ))}
  </ol>
);

/**
 * Page hero: title, a short intro, and (where we have one) the how-to video
 * framed at the top of the page.
 */
export const DocHero: React.FC<{
  title: string;
  intro: React.ReactNode;
  videoSrc?: string;
  videoCaption?: string;
}> = ({ title, intro, videoSrc, videoCaption }) => (
  <>
    <h1 className="font-display text-[28px] md:text-[32px] font-extrabold text-content-primary mb-[12px] tracking-[-0.5px]">
      {title}
    </h1>
    <p className="text-[15px] leading-[1.7] text-content-secondary mb-[24px] max-w-[680px]">
      {intro}
    </p>
    {videoSrc && (
      <figure className="m-0 mb-[8px]">
        <video
          controls
          playsInline
          preload="metadata"
          className="w-full rounded-[14px] border border-surface-hover bg-black shadow-[0_6px_24px_rgba(0,0,0,0.10)]"
        >
          <source src={videoSrc} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        {videoCaption && (
          <figcaption className="mt-[10px] text-[13px] text-slate-500 leading-[1.6]">
            {videoCaption}
          </figcaption>
        )}
      </figure>
    )}
  </>
);

/**
 * The settings present on every subdivision component (skeletal, cache, static).
 * The `name`, `type` and `example` columns are the literal Unreal property
 * name, type and default — never translated. Only the descriptions are copy.
 */
export const KeySettings: React.FC<{ image?: string }> = ({ image }) => {
  const { t } = useTranslation();
  return (
    <>
      <H2 id="key-settings">{t('subdivsDocs.layout.keySettings')}</H2>
      {image && <DocImage src={image} alt={t('subdivsDocs.shared.keySettingsAlt')} />}

      <VarCard name="Enable Subdivision" type="bool" example="true">
        {t('subdivsDocs.shared.enableSubdivision')}
      </VarCard>
      <VarCard name="Subdivision Level" type="0–10" example="1">
        {t('subdivsDocs.shared.subdivisionLevel')}
      </VarCard>
      <VarCard name="Subdivision Scheme" type="enum" example="CatmullClark">
        <Trans
          i18nKey="subdivsDocs.shared.subdivisionScheme"
          components={{
            cc: <strong>Catmull-Clark</strong>,
            lp: <strong>Loop</strong>,
            bl: <strong>Bilinear</strong>
          }}
        />
      </VarCard>
      <VarCard name="UV Interpolation" type="enum" example="SmoothCornersPlus2">
        {t('subdivsDocs.shared.uvInterpolation')}
      </VarCard>
      <VarCard name="Vertex Boundary" type="enum" example="SharpEdgesAndCorners">
        {t('subdivsDocs.shared.vertexBoundary')}
      </VarCard>
      <VarCard name="Propagate Vertex Colors" type="bool" example="true">
        {t('subdivsDocs.shared.propagateVertexColors')}
      </VarCard>
      <VarCard name="Quad Wireframe" type="bool" example="true">
        <Trans
          i18nKey="subdivsDocs.shared.quadWireframe"
          components={{ wf: <strong>Wireframe</strong> }}
        />
      </VarCard>
      <VarCard name="Motion Blur" type="bool" example="true">
        {t('subdivsDocs.shared.motionBlur')}
      </VarCard>

      <H3>{t('subdivsDocs.shared.lod')}</H3>
      <VarCard name="Auto Distance LOD" type="bool" example="false">
        {t('subdivsDocs.shared.autoDistanceLod')}
      </VarCard>
      <VarCard name="Max Subdivision Distance" type="cm" example="2000">
        <Trans
          i18nKey="subdivsDocs.shared.maxSubdivisionDistance"
          components={{ adl: <strong>Auto Distance LOD</strong> }}
        />
      </VarCard>
      <VarCard name="LOD Hysteresis" type="cm" example="200">
        {t('subdivsDocs.shared.lodHysteresis')}
      </VarCard>
    </>
  );
};

/** Quad-cage recovery, the Quad Topology panel, and the Warnings panel. */
export const QuadCages: React.FC<{
  alembicTip?: boolean;
  naniteWarning?: boolean;
}> = ({ alembicTip, naniteWarning }) => {
  const { t } = useTranslation();
  return (
    <>
      <H2 id="quad-cages">{t('subdivsDocs.layout.quadCages')}</H2>
      <DocImage
        src={quadTopologyImg}
        alt={t('subdivsDocs.shared.quadTopologyAlt')}
      />
      <Body>
        <Trans
          i18nKey="subdivsDocs.shared.quadIntro1"
          components={{
            // Plain fragments, not <code>: the source kept these inline in the
            // prose. They are slots only so the file extensions cannot be
            // translated or transliterated.
            usd: <>.usd</>,
            abc: <>.abc</>,
            fbx: <>.fbx</>,
            qtd: <strong>Quad Topology Data</strong>
          }}
        />
      </Body>
      <Body>{t('subdivsDocs.shared.quadIntro2')}</Body>
      <Body>
        <Trans
          i18nKey="subdivsDocs.shared.quadPanel"
          components={{ qt: <strong>Quad Topology</strong> }}
        />
      </Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="subdivsDocs.shared.quadRecover"
            components={{ b: <strong>Recover</strong> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="subdivsDocs.shared.quadRecoverFile"
            components={{ b: <strong>Recover from file</strong> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="subdivsDocs.shared.quadClear"
            components={{ b: <strong>Clear</strong> }}
          />
        </li>
      </ul>
      <Body>{t('subdivsDocs.shared.quadFallback')}</Body>
      {naniteWarning && (
        <Warning label={t('subdivsDocs.shared.naniteLabel')}>
          {t('subdivsDocs.shared.naniteBody')}
        </Warning>
      )}
      {alembicTip && (
        <Note label={t('subdivsDocs.shared.alembicTipLabel')}>
          <Trans
            i18nKey="subdivsDocs.shared.alembicTipBody"
            components={{ b: <strong>Store Imported Vertex Numbers</strong> }}
          />
        </Note>
      )}
    </>
  );
};

/** Sequencer keying note (skeletal + geometry cache components). */
export const SequencerNote: React.FC = () => (
  <>
    <H2 id="sequencer">Sequencer</H2>
    <Body>
      <Trans
        i18nKey="subdivsDocs.shared.sequencerBody"
        components={{
          es: <strong>Enable Subdivision</strong>,
          sl: <strong>Subdivision Level</strong>
        }}
      />
    </Body>
  </>
);

const COMPONENT_PAGES = [
  {
    to: '/squarebit-subdivs/docs/geometry-caches',
    labelKey: 'subdivsDocs.layout.geometryCaches'
  },
  {
    to: '/squarebit-subdivs/docs/skeletal-meshes',
    labelKey: 'subdivsDocs.layout.skeletalMeshes'
  },
  {
    to: '/squarebit-subdivs/docs/static-meshes',
    labelKey: 'subdivsDocs.layout.staticMeshes'
  },
  { to: '/squarebit-subdivs/docs/grooms', labelKey: 'subdivsDocs.layout.grooms' }
];

const FOOTER_LINK = 'text-blue-link no-underline font-medium';

/** Footer with links to the sibling component pages + setup docs. */
export const DocFooter: React.FC<{ current: string }> = ({ current }) => {
  const { t } = useTranslation();
  return (
    <div className="border-t border-surface-hover mt-[44px] pt-[22px]">
      <p className="text-[12px] font-semibold tracking-[0.05em] uppercase text-slate-400 mb-[10px]">
        {t('subdivsDocs.shared.otherComponents')}
      </p>
      <div className="flex flex-wrap gap-[8px] mb-[18px]">
        {COMPONENT_PAGES.filter((p) => p.to !== current).map((p) => (
          <Link
            key={p.to}
            to={p.to}
            className="text-[13px] font-medium text-blue-link no-underline px-[12px] py-[6px] rounded-[8px] border border-surface-hover hover:border-blue transition-colors"
          >
            {t(p.labelKey)} →
          </Link>
        ))}
      </div>
      <p className="text-[13px] text-slate-500 leading-[1.6]">
        <Trans
          i18nKey="subdivsDocs.shared.needSetupHelp"
          components={{
            // NB: never name a slot after an HTML void element (`link`, `img`,
            // `col`, ...) — a paired tag would parse as self-closing and the
            // link text would be dropped.
            install: (
              <Link
                to="/squarebit-subdivs/docs/installation"
                className={FOOTER_LINK}
              >
                {t('subdivsDocs.layout.installation')}
              </Link>
            ),
            lic: (
              <Link
                to="/squarebit-subdivs/docs/licensing"
                className={FOOTER_LINK}
              >
                {t('subdivsDocs.layout.licensing')}
              </Link>
            ),
            contact: <Link to="/contact" className={FOOTER_LINK} />
          }}
        />
      </p>
    </div>
  );
};
